import React, { useState } from 'react';
import { getChildValueAsString } from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { TextField } from '../inputs/TextField';
import { useDebounceStringProperty } from '../../hooks/useDebounceStringProperty';
import { getFieldType } from './utils/InputTypeUtils';
import { useEditorId } from './hooks/useEditorId';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { EditorLabel } from './EditorLabel';

export interface IEditorTextFieldProps extends IEditorProps {
  placeholder?: string;
  externalValue?: string;
  children?: React.ReactNode;
  /**
   * CSS Class to be added to outer-most element of field, whether that's a label wrapper or
   * the input itself.
   */
  className?: string;
  inputType?: string;
  /** Helper function to ensure field data conforms to required format */
  formatHelper?: (value: string) => string | null;
  /** Helper function to ensure falsy values are initialized according to field requirements. */
  initialValueHelper?: (value: string | null) => string;
}

const getValue = (fieldValue: string, value?: string) => (typeof value === 'string' ? value : fieldValue);

/**
 * A Text Field consuming a property child model.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function EditorTextField({
  autoFocus,
  child,
  children,
  className,
  id,
  inputType,
  hideInputLabel,
  path,
  onChange,
  placeholder,
  externalValue,
  formatHelper,
  initialValueHelper,
}: IEditorTextFieldProps) {
  [id] = useEditorId(path, id);
  const validation = getEditorValidationSummary(child);
  const [focused, setFocused] = useState(false);
  const { fieldValue, handleChange, emitOnBlur } = useDebounceStringProperty({
    value: getChildValueAsString(child),
    path,
    formatHelper,
    initialValueHelper,
    onChange,
    skipValueUpdate: focused,
  });

  const onFocus = () => {
    setFocused(true);
  };

  const onBlur = () => {
    setFocused(false);
    emitOnBlur();
  };

  return (
    <EditorLabel editor={child} path={path} htmlFor={id} className={className} hideInputLabel={hideInputLabel}>
      <TextField
        autoFocus={autoFocus}
        id={id}
        value={getValue(fieldValue, externalValue)}
        onChange={handleChange}
        placeholder={placeholder}
        type={inputType || getFieldType(child)}
        validation={validation}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {children}
    </EditorLabel>
  );
}
